import { useState, useEffect, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import api from 'services/api'
import useToast from 'hooks/useToast'
import Heading from './Heading'

const isVideo = (url) => /\.mp4($|\?)/i.test(url)

const StyleCover = ({ src, alt, size }) =>
  isVideo(src) ? (
    <Box
      component="video"
      src={src}
      autoPlay
      loop
      muted
      playsInline
      sx={{ width: size, height: size, borderRadius: 1, objectFit: 'cover' }}
    />
  ) : (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ width: size, height: size, borderRadius: 1, objectFit: 'cover' }}
    />
  )

const StyleRow = ({
  style,
  isChanged,
  onToggle,
  onPin,
  onUnpin,
  onMoveUp,
  onMoveDown,
  isPinned,
}) => {
  const [pinHours, setPinHours] = useState('24')
  const [markNew, setMarkNew] = useState(false)

  const pinExpiry = style.pinned_until
    ? new Date(style.pinned_until).toLocaleString()
    : null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: isChanged ? 'warning.main' : 'transparent',
        bgcolor: isChanged ? 'action.hover' : 'transparent',
      }}
    >
      <StyleCover src={style.cover_url} alt={style.display_name} size={48} />
      <Box sx={{ minWidth: 140 }}>
        <Typography variant="body2" fontWeight={700}>
          {style.display_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {style.endpoint}{' '}
          {style.animation_type ? `(${style.animation_type})` : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption">Staff</Typography>
        <Switch
          size="small"
          checked={style.staff_enabled}
          onChange={() => onToggle(style.style, 'staff_enabled')}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="caption"
          color={!style.staff_enabled ? 'text.disabled' : undefined}
        >
          Users
        </Typography>
        <Switch
          size="small"
          checked={style.user_enabled}
          disabled={!style.staff_enabled}
          onChange={() => onToggle(style.style, 'user_enabled')}
        />
      </Box>

      {isPinned ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="PINNED" color="primary" size="small" />
          {style.is_new && <Chip label="NEW" color="secondary" size="small" />}
          <Typography variant="caption">until {pinExpiry}</Typography>
          <IconButton size="small" onClick={() => onMoveUp(style.style)}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onMoveDown(style.style)}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            color="error"
            onClick={() => onUnpin(style.style)}
          >
            Unpin
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            label="Hours"
            type="number"
            value={pinHours}
            onChange={(e) => setPinHours(e.target.value)}
            sx={{ width: 80 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={markNew}
                onChange={(e) => setMarkNew(e.target.checked)}
              />
            }
            label={<Typography variant="caption">NEW</Typography>}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => onPin(style.style, pinHours, markNew)}
          >
            Pin
          </Button>
        </Box>
      )}
    </Box>
  )
}

const PreviewCard = ({ style }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 1,
      px: 2,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <StyleCover src={style.cover_url} alt={style.display_name} size={40} />
    <Typography variant="body2" fontWeight={600}>
      {style.display_name}
    </Typography>
    {style.is_new && <Chip label="NEW" color="secondary" size="small" />}
  </Box>
)

const AnimationStyles = () => {
  const toast = useToast()
  const [serverStyles, setServerStyles] = useState([])
  const [localStyles, setLocalStyles] = useState([])
  const [aiPreview, setAiPreview] = useState([])
  const [asmrPreview, setAsmrPreview] = useState([])
  const [emoticonPreview, setEmoticonPreview] = useState([])
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [pendingPins, setPendingPins] = useState({})
  const [pendingUnpins, setPendingUnpins] = useState({})
  const [pendingPinOrder, setPendingPinOrder] = useState(null)

  const load = useCallback(async () => {
    try {
      const [settingsData, aiPreviewData, asmrPreviewData, emoticonPreviewData] =
        await Promise.all([
          api.animatedVideos.getStyleSettings(),
          api.animatedVideos.getStylesPreview(),
          api.animatedVideos.getAsmrStylesPreview(),
          api.animatedVideos.getEmoticonStylesPreview(),
        ])
      setServerStyles(settingsData)
      setLocalStyles(JSON.parse(JSON.stringify(settingsData)))
      setAiPreview(aiPreviewData)
      setAsmrPreview(asmrPreviewData)
      setEmoticonPreview(emoticonPreviewData)
      setPendingPins({})
      setPendingUnpins({})
      setPendingPinOrder(null)
    } catch (e) {
      toast({ message: e.message, severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    load()
  }, [load])

  const isStyleChanged = useCallback(
    (styleKey) => {
      if (pendingPins[styleKey]) return true
      if (pendingUnpins[styleKey]) return true
      const server = serverStyles.find((s) => s.style === styleKey)
      const local = localStyles.find((s) => s.style === styleKey)
      if (!server || !local) return false
      return (
        local.staff_enabled !== server.staff_enabled ||
        local.user_enabled !== server.user_enabled ||
        local.is_new !== server.is_new ||
        Boolean(local.pinned_until) !== Boolean(server.pinned_until) ||
        local.pin_order !== server.pin_order
      )
    },
    [serverStyles, localStyles, pendingPins, pendingUnpins]
  )

  const pendingCount = useMemo(() => {
    let count = 0
    for (const s of localStyles) {
      if (isStyleChanged(s.style)) count++
    }
    return count
  }, [localStyles, isStyleChanged])

  const hasPendingChanges = pendingCount > 0

  const handleToggle = (styleKey, field) => {
    setLocalStyles((prev) =>
      prev.map((s) => {
        if (s.style !== styleKey) return s
        const updated = { ...s, [field]: !s[field] }
        if (field === 'staff_enabled' && !updated.staff_enabled) {
          updated.user_enabled = false
        }
        return updated
      })
    )
  }

  const handlePin = (styleKey, pinHours, markNew) => {
    const hours = parseFloat(pinHours) || 24
    setPendingPins((prev) => ({
      ...prev,
      [styleKey]: { pin_duration_hours: hours, is_new: markNew || false },
    }))
    setPendingUnpins((prev) => {
      const next = { ...prev }
      delete next[styleKey]
      return next
    })
    setLocalStyles((prev) => {
      const otherPinned = prev.filter(
        (s) =>
          s.style !== styleKey &&
          s.pinned_until &&
          new Date(s.pinned_until) > new Date()
      )
      const maxOrder =
        otherPinned.length > 0
          ? Math.max(...otherPinned.map((s) => s.pin_order ?? 0))
          : -1
      return prev.map((s) => {
        if (s.style !== styleKey) return s
        return {
          ...s,
          pinned_until: new Date(
            Date.now() + hours * 3600 * 1000
          ).toISOString(),
          pin_order: maxOrder + 1,
          is_new: markNew ? true : s.is_new,
        }
      })
    })
  }

  const handleUnpin = (styleKey) => {
    const server = serverStyles.find((s) => s.style === styleKey)
    const wasServerPinned =
      server?.pinned_until && new Date(server.pinned_until) > new Date()
    setPendingPins((prev) => {
      const next = { ...prev }
      delete next[styleKey]
      return next
    })
    if (wasServerPinned) {
      setPendingUnpins((prev) => ({ ...prev, [styleKey]: true }))
    }
    setPendingPinOrder((prev) => {
      if (!prev) return null
      const filtered = prev.filter((s) => s !== styleKey)
      return filtered.length > 0 ? filtered : null
    })
    setLocalStyles((prev) =>
      prev.map((s) => {
        if (s.style !== styleKey) return s
        return { ...s, pinned_until: null, pin_order: null, is_new: false }
      })
    )
  }

  const pinnedStyles = localStyles
    .filter((s) => s.pinned_until && new Date(s.pinned_until) > new Date())
    .sort((a, b) => (a.pin_order ?? 0) - (b.pin_order ?? 0))

  const unpinnedStyles = localStyles.filter(
    (s) => !s.pinned_until || new Date(s.pinned_until) <= new Date()
  )

  const handleMove = (styleKey, direction) => {
    const order = pinnedStyles.map((s) => s.style)
    const idx = order.indexOf(styleKey)
    if (idx < 0) return
    const newIdx = idx + direction
    if (newIdx < 0 || newIdx >= order.length) return
    ;[order[idx], order[newIdx]] = [order[newIdx], order[idx]]
    setPendingPinOrder(order)
    setLocalStyles((prev) =>
      prev.map((s) => {
        const pinIdx = order.indexOf(s.style)
        if (pinIdx >= 0) return { ...s, pin_order: pinIdx }
        return s
      })
    )
  }

  const handleApply = async () => {
    setApplying(true)
    const errors = []
    try {
      for (const local of localStyles) {
        const server = serverStyles.find((s) => s.style === local.style)
        if (!server) continue
        const payload = {}
        if (local.staff_enabled !== server.staff_enabled)
          payload.staff_enabled = local.staff_enabled
        if (local.user_enabled !== server.user_enabled)
          payload.user_enabled = local.user_enabled
        if (local.is_new !== server.is_new) payload.is_new = local.is_new
        const pinData = pendingPins[local.style]
        if (pinData) {
          payload.pin_duration_hours = pinData.pin_duration_hours
          if (pinData.is_new) payload.is_new = true
        }
        if (Object.keys(payload).length > 0) {
          try {
            await api.animatedVideos.updateStyleSettings(local.style, payload)
          } catch (e) {
            errors.push(local.display_name || local.style)
          }
        }
      }
      for (const styleKey of Object.keys(pendingUnpins)) {
        try {
          await api.animatedVideos.removeStylePin(styleKey)
        } catch (e) {
          errors.push(styleKey)
        }
      }
      if (pendingPinOrder) {
        try {
          await api.animatedVideos.reorderPinnedStyles(pendingPinOrder)
        } catch (e) {
          errors.push('pin reorder')
        }
      }
      if (errors.length > 0) {
        toast({
          message: `Failed to update: ${errors.join(', ')}`,
          severity: 'error',
        })
      } else {
        toast({ message: 'Changes applied successfully', severity: 'success' })
      }
    } finally {
      await load()
      setApplying(false)
    }
  }

  const handleDiscard = () => {
    setLocalStyles(JSON.parse(JSON.stringify(serverStyles)))
    setPendingPins({})
    setPendingUnpins({})
    setPendingPinOrder(null)
  }

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        pb: hasPendingChanges ? 10 : 0,
      }}
    >
      <Box sx={{ width: 900, p: 6 }}>
        <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Heading text="Emoticon Experiment Preview" />
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {emoticonPreview.length === 0 ? (
                <Typography sx={{ p: 2 }} color="text.secondary">
                  No styles visible to users.
                </Typography>
              ) : (
                emoticonPreview.map((s) => (
                  <PreviewCard key={s.style} style={s} />
                ))
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Heading text="AI Experiment Preview" />
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {aiPreview.length === 0 ? (
                <Typography sx={{ p: 2 }} color="text.secondary">
                  No styles visible to users.
                </Typography>
              ) : (
                aiPreview.map((s) => <PreviewCard key={s.style} style={s} />)
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Heading text="ASMR Experiment Preview" />
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {asmrPreview.length === 0 ? (
                <Typography sx={{ p: 2 }} color="text.secondary">
                  No styles visible to users.
                </Typography>
              ) : (
                asmrPreview.map((s) => <PreviewCard key={s.style} style={s} />)
              )}
            </Box>
          </Box>
        </Box>

        <Heading text="Style Management" />

        {pinnedStyles.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Pinned
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
              }}
            >
              {pinnedStyles.map((s) => (
                <StyleRow
                  key={s.style}
                  style={s}
                  isChanged={isStyleChanged(s.style)}
                  isPinned
                  onToggle={handleToggle}
                  onPin={handlePin}
                  onUnpin={handleUnpin}
                  onMoveUp={(style) => handleMove(style, -1)}
                  onMoveDown={(style) => handleMove(style, 1)}
                />
              ))}
            </Box>
          </>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          All Styles
        </Typography>
        <Box
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          {unpinnedStyles.map((s) => (
            <StyleRow
              key={s.style}
              style={s}
              isChanged={isStyleChanged(s.style)}
              isPinned={false}
              onToggle={handleToggle}
              onPin={handlePin}
              onUnpin={handleUnpin}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
            />
          ))}
        </Box>
      </Box>

      {hasPendingChanges && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 2,
            px: 4,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: 6,
            zIndex: 1200,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleDiscard}
            disabled={applying}
          >
            Discard
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={applying}>
            {applying ? 'Applying...' : 'Apply Changes'}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default AnimationStyles
